import type {
  SankeyLayoutLink,
  SankeyLayoutNode,
  SankeyNeighborEntry,
  SankeyTooltipPayload,
} from '../types/sankey-internal.js';
import { isResolvedNode } from './is-resolved-sankey-node.js';
import { stripPrivateFields } from './strip-private-fields.js';

function isLayoutLink(
  shape: SankeyLayoutNode | SankeyLayoutLink,
): shape is SankeyLayoutLink {
  return 'source' in shape;
}

function computePercentage(linkValue: number, total: number): string {
  if (total === 0) {
    return '0.0%';
  }
  return `${((linkValue / total) * 100).toFixed(1)}%`;
}

function sumLinkValues(links: SankeyLayoutLink[]): number {
  return links.reduce((sum, link) => sum + (link.value ?? 0), 0);
}

function buildSourceEntry(
  link: SankeyLayoutLink,
  totalInflow: number,
  formatter: (value: number) => string,
): SankeyNeighborEntry | undefined {
  if (!isResolvedNode(link.source)) {
    return undefined;
  }
  const neighbor = link.source;
  const linkValue = link.value ?? 0;
  const neighborInflow = sumLinkValues(neighbor.__targetLinks ?? []);
  const neighborOutflow = sumLinkValues(neighbor.__sourceLinks ?? []);
  return {
    label: neighbor.label,
    nodeId: neighbor.id,
    formattedValue: formatter(linkValue),
    formattedPercentage: computePercentage(linkValue, totalInflow),
    totalInflow: neighborInflow,
    totalOutflow: neighborOutflow,
    data: stripPrivateFields(neighbor),
  };
}

function buildTargetEntry(
  link: SankeyLayoutLink,
  totalOutflow: number,
  formatter: (value: number) => string,
): SankeyNeighborEntry | undefined {
  if (!isResolvedNode(link.target)) {
    return undefined;
  }
  const neighbor = link.target;
  const linkValue = link.value ?? 0;
  const neighborInflow = sumLinkValues(neighbor.__targetLinks ?? []);
  const neighborOutflow = sumLinkValues(neighbor.__sourceLinks ?? []);
  return {
    label: neighbor.label,
    nodeId: neighbor.id,
    formattedValue: formatter(linkValue),
    formattedPercentage: computePercentage(linkValue, totalOutflow),
    totalInflow: neighborInflow,
    totalOutflow: neighborOutflow,
    data: stripPrivateFields(neighbor),
  };
}

export const buildSankeyTooltipData = (
  shape: SankeyLayoutNode | SankeyLayoutLink,
  formatter: (value: number) => string = String,
  color: string = '',
): SankeyTooltipPayload => {
  if (isLayoutLink(shape)) {
    const sourceId = isResolvedNode(shape.source)
      ? shape.source.id
      : String(shape.source);
    const sourceLabel = isResolvedNode(shape.source)
      ? shape.source.label
      : String(shape.source);
    const targetId = isResolvedNode(shape.target)
      ? shape.target.id
      : String(shape.target);
    const targetLabel = isResolvedNode(shape.target)
      ? shape.target.label
      : String(shape.target);
    const value = shape.value ?? 0;
    return {
      type: 'SankeyLink',
      sourceId,
      sourceLabel,
      targetId,
      targetLabel,
      value,
      formattedValue: formatter(value),
      data: stripPrivateFields({
        ...shape,
        source: sourceId,
        target: targetId,
      }),
    };
  }

  const targetLinks = shape.__targetLinks ?? [];
  const sourceLinks = shape.__sourceLinks ?? [];
  const totalInflow = sumLinkValues(targetLinks);
  const totalOutflow = sumLinkValues(sourceLinks);

  const sources = targetLinks
    .map((link) => buildSourceEntry(link, totalInflow, formatter))
    .filter((entry): entry is SankeyNeighborEntry => entry !== undefined);

  const targets = sourceLinks
    .map((link) => buildTargetEntry(link, totalOutflow, formatter))
    .filter((entry): entry is SankeyNeighborEntry => entry !== undefined);

  const nodeVolume = Math.max(totalInflow, totalOutflow);

  return {
    type: 'SankeyNode',
    nodeId: shape.id,
    label: shape.label,
    color,
    totalInflow,
    totalOutflow,
    formattedValue: formatter(nodeVolume),
    formattedInflow: formatter(totalInflow),
    formattedOutflow: formatter(totalOutflow),
    sources,
    targets,
    data: stripPrivateFields(shape),
  };
};
